/**
 * recommendationPerformanceService.ts
 * P3.3: Client-side service for fetching recommendation performance data.
 *
 * Calls GET /lender/recommendation-performance to retrieve aggregate
 * performance metrics (CTR, classification, recency) per recommendation_key.
 *
 * Privacy: No member PII. Only aggregate event counts. Tenant-scoped.
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;

/** Classification tiers from P3.2/P3.2.1 */
export type PerformanceTier = 'HIGH_PERFORMING' | 'MEDIUM' | 'LOW' | 'INSUFFICIENT';

/** Single recommendation performance summary row */
export interface RecommendationPerformanceSummary {
  recommendation_key: string;
  total_shown: number;
  total_clicked: number;
  total_dismissed: number;
  total_accepted: number;
  ctr: number;
  dismissal_rate: number;
  acceptance_rate: number;
  last_7d_shown: number;
  last_7d_ctr: number;
  last_30d_shown: number;
  last_30d_ctr: number;
  classification: PerformanceTier;
  min_sample_size: number;
  last_event_at: string;
}

/** Full API response shape */
export interface RecommendationPerformanceResponse {
  tenant_id: string;
  summary: RecommendationPerformanceSummary[];
  trends: Array<{
    recommendation_key: string;
    event_date: string;
    shown: number;
    clicked: number;
    dismissed: number;
    accepted: number;
    ctr: number;
  }>;
  fetched_at: string;
}

/** Lookup map: recommendation_key → performance summary */
export type PerformanceMap = Map<string, RecommendationPerformanceSummary>;

/**
 * Fetch recommendation performance data from the API.
 * Returns null on error (non-blocking — performance data is optional).
 */
export async function fetchRecommendationPerformance(): Promise<{
  data: RecommendationPerformanceResponse | null;
  error: Error | null;
}> {
  try {
    if (import.meta.env.DEV) console.log('📊 [performanceService] Fetching recommendation performance...');

    const response = await fetch(`${API_BASE}/verascore/lender/recommendation-performance`, {
      method: 'GET',
      headers: {
        'VS-Client-Id': VS_CLIENT_ID,
        'Content-Type': 'application/json',
        'x-request-purpose': 'recommendation_optimization',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      if (import.meta.env.DEV) console.warn(`[performanceService] API returned ${response.status}`);
      return { data: null, error: new Error(`API error ${response.status}`) };
    }

    const json = await response.json();
    const result: RecommendationPerformanceResponse = json?.data || json;

    if (import.meta.env.DEV) {
      console.log(`📊 [performanceService] Loaded ${result.summary?.length || 0} recommendation performance entries`);
    }

    return { data: result, error: null };
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[performanceService] Failed to fetch:', err);
    return { data: null, error: err instanceof Error ? err : new Error(String(err)) };
  }
}

/**
 * Build a lookup map from the API response for fast access by recommendation key.
 * Maps behavioral action IDs (e.g., 'behavioral-high-risk-intervention') to their
 * recommendation_key equivalents (e.g., 'HIGH_RISK_INTERVENTION').
 */
export function buildPerformanceMap(
  response: RecommendationPerformanceResponse | null,
): PerformanceMap {
  const map: PerformanceMap = new Map();
  if (!response?.summary) return map;

  for (const row of response.summary) {
    // Store by raw recommendation_key
    map.set(row.recommendation_key, row);
    // Also store by behavioral action ID format for easy lookup
    const actionId = `behavioral-${row.recommendation_key.toLowerCase().replace(/_/g, '-')}`;
    map.set(actionId, row);
  }

  return map;
}
